import Alert from "@imminently/decisively-ui/Alert";
import Button from "@imminently/decisively-ui/Button";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import { lazy, Suspense } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

import { AdapterGuideFrame } from "@/features/adapters/components/AdapterGuideFrame";
import { useOptionalAdapterWorkflow } from "@/features/adapters/context/AdapterWorkflowContext";
import { useAdapterGuideResize } from "@/features/adapters/useAdapterGuideResize";

const AdapterWorkflowPanel = lazy(() =>
  import("./AdapterWorkflowPanel").then((module) => ({ default: module.AdapterWorkflowPanel })),
);

/** The guide's panel at its remembered width, with a spinner while the guide's code loads. */
const AdapterGuideLoading = () => {
  const resize = useAdapterGuideResize();
  return (
    <AdapterGuideFrame resize={resize} asideProps={{ "aria-label": "Adapter guide", "aria-busy": true }}>
      <div className="flex flex-1 items-center justify-center">
        <LoadingSpinner />
      </div>
    </AdapterGuideFrame>
  );
};

const onGuideError = (error: Error, info: { componentStack?: string | null }) => {
  console.error("Adapter guide failed to render", error, info.componentStack);
};

/** Shown in the guide's place when it fails to render, so the page beside it keeps working. */
const AdapterGuideError = ({ resetErrorBoundary }: FallbackProps) => {
  const resize = useAdapterGuideResize();
  const workflow = useOptionalAdapterWorkflow();
  return (
    <AdapterGuideFrame resize={resize} asideProps={{ "aria-label": "Adapter guide" }}>
      <div className="grid gap-3 p-4">
        <Alert
          variant="destructive"
          title="The adapter guide couldn't load"
          description="Something went wrong showing this step. The page beside it still works."
        />
        <div className="flex gap-2">
          <Button label="Try loading the adapter guide again" variant="outline" size="sm" onClick={resetErrorBoundary}>
            Try again
          </Button>
          <Button
            label="Close the adapter guide"
            variant="outline"
            size="sm"
            onClick={() => {
              workflow?.closeAdapter();
              resetErrorBoundary();
            }}
          >
            Close guide
          </Button>
        </div>
      </div>
    </AdapterGuideFrame>
  );
};

/**
 * Loads the guide only while one is open, so pages without an open guide show nothing in its place. Reopening a
 * remembered guide is handled by the sidebar item, which sets `isOpen`.
 */
export const LazyAdapterWorkflowPanel = () => {
  const workflow = useOptionalAdapterWorkflow();
  if (!workflow?.isOpen) return null;

  return (
    // Moving to another step or adapter tries again.
    <ErrorBoundary FallbackComponent={AdapterGuideError} onError={onGuideError} resetKeys={[workflow.definitionId, workflow.stepKey]}>
      <Suspense fallback={<AdapterGuideLoading />}>
        <AdapterWorkflowPanel />
      </Suspense>
    </ErrorBoundary>
  );
};
